// src/pages/AdminBillingOpsPage.jsx
import React, {useCallback, useMemo, useState} from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import {httpsCallable} from "firebase/functions";
import {doc, getDoc} from "firebase/firestore";
import {collection, getDocs, query, where, limit} from "firebase/firestore";
import {functions, db} from "../firebase";
import {planConfigs} from "../pricingtable/planConfigs";

export default function AdminBillingOpsPage() {
  const [orgId, setOrgId] = useState("");
  const [loadingOrg, setLoadingOrg] = useState(false);
  const [org, setOrg] = useState(null);

  const [planKey, setPlanKey] = useState("pro");
  const [interval, setInterval] = useState("monthly");
  const [seats, setSeats] = useState(10);
  const [daysUntilDue, setDaysUntilDue] = useState(30);
  const [sendEmail, setSendEmail] = useState(false);

  // ─────────────────────────────────────────────────────────────
  // Billing address (staff-entered)
  // Stored on organizations/{orgId}.billingAddress AND pushed to Stripe.
  // ─────────────────────────────────────────────────────────────
  const [billingAddress, setBillingAddress] = useState({
    line1: "",
    line2: "",
    city: "",
    state: "",
    postal_code: "",
    country: "US",
  });

  const [savingAddress, setSavingAddress] = useState(false);
  const [addressSavedAt, setAddressSavedAt] = useState(null);

  const [creating, setCreating] = useState(false);
  const [result, setResult] = useState(null);
  const [orders, setOrders] = useState([]);
  const [error, setError] = useState("");

  const basePlans = useMemo(() => {
    return Object.entries(planConfigs || {})
        .filter(([, cfg]) => cfg?.kind === "base")
        .map(([key]) => key);
  }, []);

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
    } catch (_) {
      // fallback
      try {
        const ta = document.createElement("textarea");
        ta.value = text;
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
      } catch (e) {
        // ignore
      }
    }
  };

  const downloadPdfFromBase64 = (base64, fileName) => {
    if (!base64) return;

    const byteChars = atob(base64);
    const byteNums = new Array(byteChars.length);

    for (let i = 0; i < byteChars.length; i += 1) {
      byteNums[i] = byteChars.charCodeAt(i);
    }

    const bytes = new Uint8Array(byteNums);
    const blob = new Blob([bytes], {type: "application/pdf"});
    const url = window.URL.createObjectURL(blob);

    const a = document.createElement("a");
    a.href = url;
    a.download = fileName || "quote.pdf";
    document.body.appendChild(a);
    a.click();
    a.remove();

    window.URL.revokeObjectURL(url);
  };

  const stripeSearchUrl = (queryText) => {
    if (!queryText) return null;
    return "https://dashboard.stripe.com/test/search?query=" +
      encodeURIComponent(queryText);
  };

  const fetchQuotePdfForOrder = async (orderId) => {
    const callable = httpsCallable(functions, "getQuotePdfForOrder");
    const {data} = await callable({orderId});
    return data;
  };

  const loadOrg = useCallback(async () => {
    setError("");
    setOrg(null);
    setResult(null);
    setOrders([]);

    const id = (orgId || "").trim();
    if (!id) {
      setError("Enter an organizationId to load.");
      return;
    }

    setLoadingOrg(true);
    try {
      const ref = doc(db, "organizations", id);
      const snap = await getDoc(ref);

      if (!snap.exists()) {
        setError("Organization not found.");
        return;
      }

      const data = {id: snap.id, ...snap.data()};
      setOrg(data);

      // Hydrate billing address from org doc (if present)
      try {
        const addr = data?.billingAddress || null;
        if (addr) {
          setBillingAddress({
            line1: addr.line1 || "",
            line2: addr.line2 || "",
            city: addr.city || "",
            state: addr.state || "",
            postal_code: addr.postal_code || "",
            country: addr.country || "US",
          });
        }

        const ts = data?.billingAddressUpdatedAt || null;
        if (ts) {
          setAddressSavedAt(String(ts));
        } else {
          setAddressSavedAt(null);
        }
      } catch (_) {
        // ignore
      }


      // Fetch recent billingOrders for this org (no composite index needed).

      // We sort client-side by createdAt ISO string (safe lexicographically).
      try {
        const q = query(
            collection(db, "billingOrders"),
            where("orgId", "==", id),
            limit(25),
        );
        const osnap = await getDocs(q);

        const rows = osnap.docs.map((d) => ({id: d.id, ...d.data()}));
        rows.sort((a, b) => {
          const aa = String(a.createdAt || "");
          const bb = String(b.createdAt || "");
          return bb.localeCompare(aa);
        });

        setOrders(rows.slice(0, 10));
      } catch (e) {
        // Non-fatal: page still usable
        setOrders([]);
      }
    } catch (e) {
      setError(e?.message || "Failed to load organization.");
    } finally {
      setLoadingOrg(false);
    }
  }, [orgId]);

  const createQuote = useCallback(async () => {
    setError("");
    setResult(null);

    const id = (orgId || "").trim();
    if (!id) {
      setError("orgId is required.");
      return;
    }

    const seatsNum = Number(seats);
    const dueNum = Number(daysUntilDue);

    if (!Number.isFinite(seatsNum) || seatsNum < 1) {
      setError("Seats must be a positive number.");
      return;
    }

    if (!Number.isFinite(dueNum) || dueNum < 0) {
      setError("daysUntilDue must be 0 or greater.");
      return;
    }

    setCreating(true);
    try {
      const callable = httpsCallable(functions, "createInvoiceQuoteForOrg");

      const {data} = await callable({
        orgId: id,
        targetPlanKey: planKey,
        interval,
        seats: seatsNum,
        daysUntilDue: dueNum,
        sendEmail: sendEmail === true,
      });

      setResult(data || null);

      // Refresh orders list quickly after creation.
      try {
        await loadOrg();
      } catch (_) {
        // ignore
      }
    } catch (e) {
      setError(e?.message || "Failed to create invoice quote.");
    } finally {
      setCreating(false);
    }
  }, [
    orgId,
    planKey,
    interval,
    seats,
    daysUntilDue,
    sendEmail,
    loadOrg,
  ]);

  const saveBillingAddress = useCallback(async () => {
    setError("");

    const id = (orgId || "").trim();
    if (!id) {
      setError("orgId is required to save billing address.");
      return;
    }

    const country = (billingAddress.country || "").trim().toUpperCase();
    const postal = (billingAddress.postal_code || "").trim();

    if (!country) {
      setError("Country is required.");
      return;
    }

    if (country.length !== 2) {
      setError("Country must be a 2-letter ISO code (e.g., US).");
      return;
    }

    // Minimal requirement for US tax location
    if (country === "US" && postal.length < 5) {
      setError("ZIP code is required for US billing address.");
      return;
    }


    setSavingAddress(true);
    try {
      const callable = httpsCallable(functions, "updateBillingAddressForOrg");

      await callable({
        orgId: id,
        billingAddress: {
          ...billingAddress,
          country,
          postal_code: postal,
        },
      });

      // Reload org so the page reflects what was stored in Firestore
      // (including billingAddressUpdatedAt from the server).
      try {
        await loadOrg();
      } catch (_) {
        // ignore
      }
    } catch (e) {
      setError(e?.message || "Failed to save billing address.");
    } finally {
      setSavingAddress(false);
    }
  }, [orgId, billingAddress, loadOrg]);

  const orgSummary = org ? {
    name: org.name || "—",
    stripeCustomerId: org.stripeCustomerId || "—",
    planKey: org.planKey || "—",
    priceId: org.priceId || "—",
    seats: org.seatCount ?? "—",
    status: org.subscriptionStatus || "—",
  } : null;

  return (
    <Box sx={{p: 3}}>
      <Stack spacing={2}>
        <Typography variant="h5">
          Billing Ops
        </Typography>

        <Alert severity="info">
          Staff-only tools for sales-led invoicing. Self-serve customers are not
          affected by actions taken here.
        </Alert>

        {error ? <Alert severity="error">{error}</Alert> : null}

        <Card>
          <CardContent>
            <Stack spacing={2}>
              <Typography variant="subtitle1">
                Find organization
              </Typography>

              <Stack
                direction={{xs: "column", md: "row"}}
                spacing={2}
                alignItems="stretch"
              >
                <TextField
                  label="Organization ID"
                  value={orgId}
                  onChange={(e) => setOrgId(e.target.value)}
                  placeholder="e.g. 2XZ2LYUSsMgv369V88BN"
                  fullWidth
                />
                <Button
                  variant="outlined"
                  onClick={loadOrg}
                  disabled={loadingOrg}
                >
                  {loadingOrg ? "Loading..." : "Load"}
                </Button>
              </Stack>

              {orgSummary ? (
                <>
                  <Divider />
                  <Typography variant="subtitle2">
                    Org summary
                  </Typography>

                  <Stack direction="row" spacing={1} flexWrap="wrap">
                    <Chip label={`Name: ${orgSummary.name}`} />
                    <Chip label={`Plan: ${orgSummary.planKey}`} />
                    <Chip label={`Seats: ${orgSummary.seats}`} />
                    <Chip label={`Status: ${orgSummary.status}`} />
                  </Stack>

                  <Stack spacing={1}>
                    <Typography variant="body2">
                      stripeCustomerId: {orgSummary.stripeCustomerId}
                    </Typography>
                    <Typography variant="body2">
                      priceId: {orgSummary.priceId}
                    </Typography>
                  </Stack>

                  <Stack direction="row" spacing={2}>
                    <Button
                      size="small"
                      startIcon={<ContentCopyIcon />}
                      onClick={() => copyToClipboard(orgSummary.stripeCustomerId)}
                      disabled={!orgSummary.stripeCustomerId ||
                        orgSummary.stripeCustomerId === "—"}
                    >
                      Copy customerId
                    </Button>

                    <Button
                      size="small"
                      startIcon={<OpenInNewIcon />}
                      onClick={() => {
                        const url = stripeSearchUrl(orgSummary.stripeCustomerId);
                        if (url) window.open(url, "_blank");
                      }}
                      disabled={!orgSummary.stripeCustomerId ||
                        orgSummary.stripeCustomerId === "—"}
                    >
                      Open customer in Stripe
                    </Button>
                  </Stack>
                </>
              ) : null}
            </Stack>
          </CardContent>
        </Card>

        <Card>
          <CardContent>
            <Stack spacing={2}>
              <Typography variant="subtitle1">
                Billing address
              </Typography>

              <Alert severity="info">
                Staff-entered billing address is used for invoices and taxes.
                For US customers, ZIP is required for tax.
              </Alert>

              <Stack direction={{xs: "column", md: "row"}} spacing={2}>
                <TextField
                  label="Address line 1"
                  value={billingAddress.line1}
                  onChange={(e) =>
                    setBillingAddress((p) => ({...p, line1: e.target.value}))
                  }
                  fullWidth
                />
                <TextField
                  label="Address line 2"
                  value={billingAddress.line2}
                  onChange={(e) =>
                    setBillingAddress((p) => ({...p, line2: e.target.value}))
                  }
                  fullWidth
                />
              </Stack>

              <Stack direction={{xs: "column", md: "row"}} spacing={2}>
                <TextField
                  label="City"
                  value={billingAddress.city}
                  onChange={(e) =>
                    setBillingAddress((p) => ({...p, city: e.target.value}))
                  }
                  fullWidth
                />
                <TextField
                  label="State / Region"
                  value={billingAddress.state}
                  onChange={(e) =>
                    setBillingAddress((p) => ({...p, state: e.target.value}))
                  }
                  fullWidth
                />
              </Stack>

              <Stack direction={{xs: "column", md: "row"}} spacing={2}>
                <TextField
                  label="Postal code"
                  value={billingAddress.postal_code}
                  onChange={(e) =>
                    setBillingAddress((p) => ({
                      ...p,
                      postal_code: e.target.value,
                    }))
                  }
                  fullWidth
                />
                <TextField
                  label="Country (2-letter)"
                  value={billingAddress.country}
                  onChange={(e) =>
                    setBillingAddress((p) => ({...p, country: e.target.value}))
                  }
                  fullWidth
                />
              </Stack>

              <Stack direction="row" spacing={2} alignItems="center">
                <Button
                  variant="contained"
                  onClick={saveBillingAddress}
                  disabled={savingAddress || !(orgId || "").trim()}
                >
                  {savingAddress ? "Saving..." : "Save billing address"}
                </Button>

                {addressSavedAt ? (
                  <Typography variant="caption" color="text.secondary">
                    Saved: {addressSavedAt}
                  </Typography>
                ) : null}
              </Stack>
            </Stack>
          </CardContent>
        </Card>

        <Card>
          <CardContent>
            <Stack spacing={2}>
              <Typography variant="subtitle1">
                Create invoice quote
              </Typography>

              <Stack direction={{xs: "column", md: "row"}} spacing={2}>
                <FormControl fullWidth>
                  <InputLabel id="planKeyLabel">Plan</InputLabel>
                  <Select
                    labelId="planKeyLabel"
                    label="Plan"
                    value={planKey}
                    onChange={(e) => setPlanKey(e.target.value)}
                  >
                    {basePlans.map((k) => (
                      <MenuItem key={k} value={k}>
                        {k}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl fullWidth>
                  <InputLabel id="intervalLabel">Interval</InputLabel>
                  <Select
                    labelId="intervalLabel"
                    label="Interval"
                    value={interval}
                    onChange={(e) => setInterval(e.target.value)}
                  >
                    <MenuItem value="monthly">monthly</MenuItem>
                    <MenuItem value="annual">annual</MenuItem>
                  </Select>
                </FormControl>
              </Stack>

              <Stack direction={{xs: "column", md: "row"}} spacing={2}>
                <TextField
                  label="Seats"
                  type="number"
                  value={seats}
                  onChange={(e) => setSeats(e.target.value)}
                  fullWidth
                />
                <TextField
                  label="Net terms (daysUntilDue)"
                  type="number"
                  value={daysUntilDue}
                  onChange={(e) => setDaysUntilDue(e.target.value)}
                  fullWidth
                />
              </Stack>

              <FormControl fullWidth>
                <InputLabel id="sendEmailLabel">Send email</InputLabel>
                <Select
                  labelId="sendEmailLabel"
                  label="Send email"
                  value={sendEmail ? "yes" : "no"}
                  onChange={(e) => setSendEmail(e.target.value === "yes")}
                >
                  <MenuItem value="no">no</MenuItem>
                  <MenuItem value="yes">yes</MenuItem>
                </Select>
              </FormControl>

              <Button
                variant="contained"
                onClick={createQuote}
                disabled={creating || !(orgId || "").trim()}
              >
                {creating ? "Creating..." : "Create quote"}
              </Button>

              {result ? (
                <>
                  <Divider />
                  <Typography variant="subtitle2">
                    Quote created
                  </Typography>

                  <Typography variant="body2">
                    orderId: {result.orderId || "—"}
                  </Typography>
                  <Typography variant="body2">
                    quoteId: {result.quoteId || "—"}
                  </Typography>
                  <Typography variant="body2">
                    planKey: {result.planKey || "—"}
                  </Typography>
                  <Typography variant="body2">
                    priceId: {result.priceId || "—"}
                  </Typography>
                  <Typography variant="body2">
                    taxSkipReason: {result.taxSkipReason || "—"}
                  </Typography>

                  <Stack direction="row" spacing={2}>
                    <Button
                      size="small"
                      startIcon={<ContentCopyIcon />}
                      onClick={() => copyToClipboard(result.quoteId || "")}
                      disabled={!result.quoteId}
                    >
                      Copy quoteId
                    </Button>

                    <Button
                      size="small"
                      startIcon={<OpenInNewIcon />}
                      onClick={() => {
                        const url = stripeSearchUrl(result.quoteId);
                        if (url) window.open(url, "_blank");
                      }}
                      disabled={!result.quoteId}
                    >
                      Open quote in Stripe
                    </Button>
                  </Stack>
                </>
              ) : null}
            </Stack>
          </CardContent>
        </Card>

        <Card>
          <CardContent>
            <Stack spacing={2}>
              <Typography variant="subtitle1">
                Recent billing activity
              </Typography>

              {orders.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No recent billingOrders found for this org.
                </Typography>
              ) : (
                <Stack spacing={1}>
                  {orders.map((o) => (
                    <Box
                      key={o.id}
                      sx={{
                        border: "1px solid",
                        borderColor: "divider",
                        borderRadius: 2,
                        p: 1.5,
                      }}
                    >
                      <Stack
                        direction={{xs: "column", md: "row"}}
                        spacing={1}
                        justifyContent="space-between"
                      >
                        <Typography variant="body2">
                          {o.type || "—"} • {o.status || "—"}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {o.createdAt || "—"}
                        </Typography>
                      </Stack>

                      <Typography variant="caption" color="text.secondary">
                        orderId: {o.id}
                      </Typography>

                      <Typography variant="caption" display="block">
                        quoteId: {o?.stripe?.quoteId || "—"}
                      </Typography>

                      <Stack direction="row" spacing={1} sx={{mt: 1}}>
                        <Button
                          size="small"
                          variant="outlined"
                          disabled={!o?.id}
                          onClick={async () => {
                            try {
                              const pdf = await fetchQuotePdfForOrder(o.id);
                              downloadPdfFromBase64(
                                  pdf?.pdfBase64 || "",
                                  pdf?.fileName || "quote.pdf",
                              );
                            } catch (e) {
                              setError(
                                e?.message ||
                                "Failed to download quote PDF.",
                              );
                            }
                          }}
                        >
                          Download PDF
                        </Button>

                        <Button
                          size="small"
                          variant="text"
                          startIcon={<OpenInNewIcon />}
                          disabled={!o?.stripe?.quoteId}
                          onClick={() => {
                            const url = stripeSearchUrl(o?.stripe?.quoteId);
                            if (url) window.open(url, "_blank");
                          }}
                        >
                          Open in Stripe
                        </Button>
                      </Stack>


                      <Typography variant="caption" display="block">
                        priceId: {o?.stripe?.priceId || "—"} • seats:{" "}
                        {o.seats ?? "—"}
                      </Typography>
                    </Box>
                  ))}
                </Stack>
              )}
            </Stack>
          </CardContent>
        </Card>
      </Stack>
    </Box>
  );
}
